import { IpaType } from "../phonemes/ipa";
import { longToMilliseconds, symbolToMilliseconds, synthSymbolsToIpa } from "./exact";

/**
 * convert ph_ipa's synth phonemes to IPA, returns [IPA-segments, unknown]
 * if asMs, convert the numbers to milliseconds (except for 50=extra-short & 100=half-long)
 */
export function convertSynthToIpa(input: string, asMs?: boolean): [IpaType[], string[]] {
  const symbolMap = getSymbolMap(asMs);
  return simpleMap(input, symbolMap);
}

/** get map from synth-symbol to IPA, optionally converting 0-9 lengths into milliseconds */
function getSymbolMap(asMs?: boolean): Record<string, IpaType> {
  if (asMs) {
    // e.g., rather than {'6': '6'}, turn it into {'6': '220'}
    const ascii0 = '0'.charCodeAt(0);
    const ms: Record<string, IpaType> = {};
    for (let i = 1; i <= 9; i++) {
      if (i !== 2) {  // 0 & 2 are defined in synthSymbolsToIpa
        const symbol = String.fromCharCode(i + ascii0);
        ms[symbol] = symbolToMilliseconds[i].toString();
      }
    }
    return { ...longToMilliseconds, ...synthSymbolsToIpa, ...ms };
  }
  return synthSymbolsToIpa;
}

/** convert 'input' by doing lookups in 'map' */
function simpleMap(input: string, record: Record<string, IpaType>): [IpaType[], string[]] {
  const ipa: IpaType[] = [];
  const unknown: string[] = [];

  const keys = getKeys(record);
  for (let i = 0; i < input.length; i++) {
    const [num, match] = findMatch(input, i, keys);
    if (num === 0) {
      unknown.push(match);
    } else {
      ipa.push(record[match]);
      i += num - 1;
    }
  }
  return [ipa, unknown];
}

function findMatch(input: string, i: number, keys: string[]): [number, string] {
  const one = input.charAt(i);
  const two = input.substring(i, i + 2);
  const three = input.substring(i, i + 3);
  const four = input.substring(i, i + 4);
  const five = input.substring(i, i + 5);
  for (let key of keys) {
    if (key.length === 1 && key === one) {
      return [1, key];
    } else if (key.length === 2 && key === two) {
      return [2, key];
    } else if (key.length === 3 && key === three) {
      return [3, key];
    } else if (key.length === 4 && key === four) {
      return [4, key];
    } else if (key.length === 5 && key === five) {
      return [5, key];
    }
  }
  return [0, one];
}

function getKeys(record: Record<string, IpaType>): string[] {
  // workaround for javascript "quirk" that moves all strings that
  // happen to be numbers to the beginning of the list, which causes
  // the lookup to happen in the wrong order
  const old = Object.getOwnPropertyNames(record);
  const keys: string[] = [];
  const numbers: string[] = [];
  for (const key of old) {
    if (key.length === 1 && '0' <= key && key <= '9') {
      numbers.push(key);
    } else {
      keys.push(key);
    }
  }
  return keys.concat(numbers);
}
